import { EventRegistry } from './event_registry';
import { Cesium } from 'app/cesium';

class CesiumTimeThrottledEventListener {
    _listener;
    _throttle;
    _lastCallTime;

    constructor(listener, throttle) {
        this._listener = listener;
        this._throttle = throttle;
        this._reset(null);
    }

    _reset(currentTime) {
        this._lastCallTime = currentTime;
    }

    fire(timestamp, ...args) {
        if (
            !this._lastCallTime ||
            Math.abs(Cesium.JulianDate.secondsDifference(timestamp, this._lastCallTime)) >=
                this._throttle
        ) {
            this._listener.call(this, timestamp, ...args);
            this._reset(timestamp);
        }
    }
}

/**
 * A different type of event registry. Strictly for use with Cesium JulianDates'
 * For example, a clock could fire an event every second tick,
 * but listeners may only want to receive every 5th second or every minute
 */
export default class CesiumTimeThrottledEventRegistry extends EventRegistry {
    private _lastArgs: any[];

    constructor() {
        super();
    }

    /**
     * Add an event listener callback
     * @param listener A function
     * @param secondsThrottle The minimum number of seconds between fires
     * @returns {function()} A function that can be used to destroy the
     * event listener
     */
    addEventListener(listener, secondsThrottle = 1) {
        const item = new CesiumTimeThrottledEventListener(listener, secondsThrottle);
        const rm = super.addEventListener(item.fire.bind(item));
        if (this._lastArgs) {
            (item.fire as any)(...this._lastArgs);
        }
        return rm;
    }

    /**
     * Notifies all listeners of the current timestamp,
     * will fire any listeners if necessary and reset their throttles to the current timestamp
     * @param timestamp JulianDate the current timestamp this event registry knows about
     * @param args Any arguments to pass to the listeners if they are called
     * @returns A promise with the results from calling all of the event handlers
     */
    notifyListeners(timestamp, ...args): Promise<any[]> {
        if (!(timestamp instanceof Cesium.JulianDate)) {
            throw new TypeError(
                'First argument of the time throttled event registry must be a Julian Date',
            );
        }
        this._lastArgs = [timestamp, ...args];
        return super.notifyListeners(timestamp, ...args);
    }
}
